<template>
    <sui-segment :loading="isLoading">
        <h1 is="sui-header">{{$t('gateway.templates.listTitle')}} <span
            v-if="$route.query.customer_name"> - {{ $route.query.customer_name }}</span></h1>
        <!-- Filters & exports -->

        <sui-card class="fluid">
            <sui-card-content>
                <sui-form @submit="(e) => e.preventDefault()">
                    <sui-form-field>
                        <label>{{ $t('gateway.templates.searchByName') }}</label>
                        <div class="ui fluid action input">
                            <sui-input v-model="searchString"/>
                            <sui-button v-on:click="onResetSearch()" icon="trash"/>
                        </div>
                    </sui-form-field>
                </sui-form>
            </sui-card-content>
        </sui-card>

        <!-- Table header -->
        <sui-table class="compact" celled striped v-if="filteredTemplates.length">
            <sui-table-header>
                <sui-table-row>
                    <sui-table-header-cell>{{ $t('common.assignment') }}</sui-table-header-cell>
                    <sui-table-header-cell>ID</sui-table-header-cell>
                    <sui-table-header-cell>{{ $t('common.name') }}</sui-table-header-cell>
                    <sui-table-header-cell>
                        <sui-button icon="minus"
                                    size="mini"
                                    v-if="isShowCollapseAllConfigurations"
                                    @click="onCollapseAllConfiguration"></sui-button>
                        {{ $t('common.configuration') }}
                    </sui-table-header-cell>
                </sui-table-row>
            </sui-table-header>

            <!-- Table Body -->

            <sui-table-body v-if="isLoading === false">
                <sui-table-row v-for="template in filteredTemplates" :key="template.id" :positive="template.is_assigned">
                    <sui-table-cell>
                        <sui-label color="green" v-if="template.is_assigned">Assigné</sui-label>
                    </sui-table-cell>
                    <sui-table-cell>{{ template.id }}</sui-table-cell>
                    <sui-table-cell>{{ template.name }}</sui-table-cell>
                    <sui-table-cell>
                        <sui-accordion v-if="template.configurations.length">
                            <sui-accordion-title @click="onToggleCollapseConfiguration(template.id)"
                                                 :active="isConfigurationOpened(template.id)">
                                <sui-icon name="dropdown"/>
                                {{ template.configurations.length }}
                            </sui-accordion-title>
                            <sui-accordion-content :active="isConfigurationOpened(template.id)">
                                <sui-table celled striped inverted>
                                    <sui-table-header>
                                        <sui-table-header-cell>ID</sui-table-header-cell>
                                        <sui-table-header-cell>Nom</sui-table-header-cell>
                                        <sui-table-header-cell>Par défaut</sui-table-header-cell>
                                        <sui-table-header-cell>Actions</sui-table-header-cell>
                                    </sui-table-header>
                                    <sui-table-body>
                                        <sui-table-row v-for="configuration in template.configurations"
                                                       :key="configuration.id">
                                            <sui-table-cell>{{ configuration.id }}</sui-table-cell>
                                            <sui-table-cell>{{ configuration.name }}</sui-table-cell>
                                            <sui-table-cell>
                                                <sui-form-field>
                                                    <sui-checkbox toggle
                                                                  :disabled="!template.is_assigned"
                                                                  @change="onIsDefaultConfigChange(template.id, configuration)"
                                                                  v-model="configuration.is_default"/>
                                                </sui-form-field>
                                            </sui-table-cell>
                                            <sui-table-cell>
                                                <sui-button size="small"
                                                            icon="edit"
                                                            @click="goToTemplateConfiguration(template, configuration.id)">
                                                </sui-button>
                                                <sui-button size="small"
                                                            color="red"
                                                            icon="trash"
                                                            @click="onDeleteConfiguration(template.id, configuration.id)">
                                                </sui-button>
                                            </sui-table-cell>
                                        </sui-table-row>
                                    </sui-table-body>
                                    <sui-table-footer full-width>
                                        <sui-table-row>
                                            <sui-table-header-cell colspan="4">
                                                <sui-button fluid size="small" labeled
                                                            :disabled="!template.is_assigned"
                                                            @click="goToTemplateConfiguration(template)">
                                                    <sui-icon name="plus"/>
                                                    {{ $t('common.noConfig') }}
                                                </sui-button>
                                            </sui-table-header-cell>
                                        </sui-table-row>
                                    </sui-table-footer>
                                </sui-table>
                            </sui-accordion-content>
                        </sui-accordion>
                        <sui-button :disabled="!template.is_assigned" fluid v-else size="small" labeled @click="goToTemplateConfiguration(template)">
                            <sui-icon name="plus"/>
                            {{ $t('gateway.templates.addConfig') }}
                        </sui-button>
                    </sui-table-cell>
                </sui-table-row>
            </sui-table-body>
        </sui-table>

        <!-- empty templates -->
        <sui-message v-if="!filteredTemplates.length" :header="$t('gateway.templates.noTemplate')"/>

    </sui-segment>
</template>
<script>
import store from '@/store';
import { api } from '@/api';

export default {
    name: 'CustomerTemplateList',
    beforeCreate() {
        store.commit('initFocusedTemplateConfigurations', this.$route.params.customer_id);
    },
    beforeDestroy() {
        store.commit('removeAppTitle');
    },
    data() {
        return {
            isLoading: true,
            templates: [],
            searchString: null,
        }
    },
    computed: {
        filteredTemplates: {
            get() {
                return this.templates
                    .filter(template => {
                    return !this.searchString || template.name.toUpperCase().includes(this.searchString.toUpperCase());
                })
                    .sort((a,b) => b.is_assigned - a.is_assigned);
            }
        },
        isConfigurationOpened() {
            return templateId => store.getters.focusedTemplateConfigurations(this.$route.params.customer_id).includes(templateId);
        },
        isShowCollapseAllConfigurations() {
            return store.getters.focusedTemplateConfigurations(this.$route.params.customer_id).length > 0;
        }
    },
    created() {
        this.getTemplates();
        store.commit('setAppTitle', `${this.$t('gateway.templates.listTitle')} - ${this.$route.query.customer_name}`);
    },
    methods: {
        async getTemplates() {
            this.isLoading = true;
            try {
                this.templates = [...await api.customers.getTemplates(this.$route.params.customer_id)];
            } catch (err) {
                this.$toastr.e(err.message, this.$t('gateway.templates.errorLoadingList'))
            } finally {
                this.isLoading = false;
            }
        },
        async updateConfigurationDefault(templateId, config) {
            try {
                return await api.customers.updateTemplateConfig(this.$route.params.customer_id, templateId, config.id);
            } catch (err) {
                this.$toastr.e(err.message, this.$t('gateway.templates.saveConfigError'));
                throw err;
            }
        },
        async deleteConfiguration(templateId, configId) {
            try {
                await api.customers.removeTemplateConfig(this.$route.params.customer_id, templateId, configId);
                this.templates = this.templates.map(template => ({
                        ...template,
                        configurations: template.configurations.filter(c => c.id !== configId)
                    })
                );
            } catch (err) {
                this.$toastr.e(err.message, this.$t('gateway.templates.deleteConfigError'));
                throw err;
            }
        },
        onResetSearch() {
            this.searchString = null;
        },
        onToggleCollapseConfiguration(templateId) {
            store.commit('toggleFocusedTemplateConfigurations', {
                customerId: this.$route.params.customer_id,
                templateId
            });
        },
        onCollapseAllConfiguration() {
            store.commit('closeAllFocusedTemplateConfigurations', this.$route.params.customer_id);
        },
        onDeleteConfiguration(templateId, configId) {
            this.$confirm({
                message: this.$t('gateway.templates.confirmDelete'),
                button: {
                    yes: this.$t('common.yes'),
                    no: this.$t('common.cancel')
                },
                callback: confirm => {
                    if (confirm) {
                        this.deleteConfiguration(templateId, configId);
                    }
                }
            })
        },
        goToTemplateConfiguration(template, configuration_id) {
            const basePath = `/customers/${this.$route.params.customer_id}/templates`
            let path = `${basePath}/${template.id}/configurations`;
            if (configuration_id) {
                path += `/${configuration_id}`
            }

            this.$router.push({path, query: {
                templateName: template.name
            }})
        },
        onIsDefaultConfigChange(templateId, config) {
            let currentTemplate = this.templates.find(t => t.id === templateId);
            const oldDefaultConfig = currentTemplate.configurations.find(c => c.id !== config.id && c.is_default);
            this.updateConfigurationDefault(templateId, config)
                .then((data) => {
                    if (oldDefaultConfig) {
                        oldDefaultConfig.is_default = false;
                    }
                    this.$toastr.s(
                        `La configuration ${data.name.bold().toUpperCase()} du template ${currentTemplate.name.bold().toUpperCase()} a été modifiée`,
                    );
                })
                .catch(() => {
                    const currentConfig = currentTemplate.configurations.find(c => c.id === config.id);
                    currentConfig.is_default = !currentConfig.is_default;
                })
        }
    }
}
</script>
